import type { KeyboardDnDHighlight } from "./keyboardDnDTypes";
import type { KeyboardDnDArenaEntry } from "./KeyboardDnDArenaRegistry";

/**
 * Pure navigation helpers for keyboard drag-and-drop (design v2).
 *
 * Arrow navigation is LINEAR over the insertion-index order the layout
 * produces (reversible, no dead arrows), CLAMPED at the edges — when
 * moved === false the caller must announce the dead end (never-silent
 * contract); silence is the worst outcome for screen-reader users.
 */

export interface HighlightStep {
  index: number;
  moved: boolean;
}

export function nextHighlightIndex(
  currentIndex: number,
  count: number,
  direction: 1 | -1,
): HighlightStep {
  if (count <= 0) {
    return { index: 0, moved: false };
  }

  const next = currentIndex + direction;

  if (next < 0 || next >= count) {
    return { index: currentIndex, moved: false };
  }

  return { index: next, moved: true };
}

/**
 * Move mode starts on the widget's current position when the layout marks
 * it (existingPositionHighlight), mirroring where the widget already is.
 */
export function findStartIndex(highlights: KeyboardDnDHighlight[]): number {
  const index = highlights.findIndex(
    (highlight) => highlight.existingPositionHighlight === true,
  );

  return index === -1 ? 0 : index;
}

/**
 * Cycles droppable arenas in registration (mount) order, wrapping. The
 * droppability verdicts (canActivate / allowToDrop) are computed by the
 * arena's own state hooks from the existing predicates
 * (canActivateCanvasForDraggedWidget, isDropTargetInsideDraggedWidgets,
 * areWidgetsWhitelisted) — they are consumed here, never re-derived.
 */
export function cycleCanvas(
  entries: KeyboardDnDArenaEntry[],
  currentLayoutId: string,
  direction: 1 | -1,
): KeyboardDnDArenaEntry | null {
  const droppable = entries.filter(
    (entry) => entry.canActivate && entry.allowToDrop,
  );

  if (droppable.length === 0) return null;

  const currentIndex = droppable.findIndex(
    (entry) => entry.layoutId === currentLayoutId,
  );

  if (currentIndex === -1) {
    return droppable[0];
  }

  if (droppable.length === 1) return null;

  const nextIndex =
    (currentIndex + direction + droppable.length) % droppable.length;

  return droppable[nextIndex];
}
